import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExternalLink, Copy, Check, Edit3, Globe, Phone, Mail, MapPin,
  Search, Users, Layout, Star, Briefcase, FileText, Link2, CheckCircle2, Settings,
  RefreshCw, Loader2, AlertCircle, Calendar,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  google_business: Search,
  website: Globe,
  facebook: Users,
  instagram: Layout,
  yelp: Star,
  nextdoor: MapPin,
  linkedin: Briefcase,
  industry_directory: FileText,
  phone: Phone,
  email: Mail,
};

export default function ProfileTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [webPresence, setWebPresence] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile/me`, { withCredentials: true });
      setData(res.data);
      if (res.data.profile) {
        setEditForm({
          bio: res.data.profile.bio || '',
          tagline: res.data.profile.tagline || '',
          phone: res.data.profile.phone || res.data.business?.phone || '',
          email: res.data.profile.email || res.data.business?.email || '',
          website: res.data.profile.website || res.data.business?.website || '',
        });
      }
      // Fetch web presence data
      try {
        const wpRes = await axios.get(`${API}/business/web-presence`, { withCredentials: true });
        if (wpRes.data?.has_data) setWebPresence(wpRes.data.web_presence);
      } catch (e) {
        // Web presence fetch error — non-critical
      }
    } catch (e) {
      if (e.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleScrape = async () => {
    const url = editForm.website || data?.business?.website || data?.profile?.website;
    if (!url) { setScrapeError('Add your website URL above first.'); return; }
    setScraping(true);
    setScrapeError(null);
    try {
      const res = await axios.post(`${API}/business/scrape-website`, { url }, { withCredentials: true });
      if (res.data?.web_presence) setWebPresence(res.data.web_presence);
    } catch (e) {
      setScrapeError(e.response?.data?.detail || 'Scrape failed. Try again later.');
    } finally {
      setScraping(false);
    }
  };

  const profileUrl = data?.slug
    ? `${window.location.origin}/p/${data.slug}`
    : null;

  const copyProfileUrl = () => {
    if (!profileUrl) return;
    navigator.clipboard.writeText(profileUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveProfile = async () => {
    try {
      await axios.put(`${API}/profile`, editForm, { withCredentials: true });
      setEditing(false);
      // Refresh
      const res = await axios.get(`${API}/profile/me`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      // Profile save failed
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.has_profile) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6">
        <Link2 size={48} strokeWidth={1} className="text-[#D4C9B8] mb-4" />
        <h2 className="font-display text-2xl text-[#3C3226] mb-2">No profile yet</h2>
        <p className="text-[#5C5347] text-center mb-6">Complete a scan to create your AI-optimized profile page.</p>
        <button onClick={() => navigate('/onboarding')} className="rounded-none bg-[#C67A5C] text-[#F0EBE3] px-8 py-3 font-medium" data-testid="create-profile-btn">
          Start monitoring
        </button>
        <AppShell active="profile" />
      </div>
    );
  }

  const { business, profile, linked_platforms, slug } = data;

  return (
    <AppShell active="profile">
    <div className="min-h-screen bg-[#F7F4EF] pb-24 desktop:pb-8" data-testid="profile-tab-page">
      <header className="px-6 pt-6 pb-2 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium text-[#3C3226]">Your Profile</h1>
          <p className="text-sm text-[#5C5347]">Manage how you appear to AI and customers</p>
        </div>
        <button
          onClick={() => navigate('/settings')}
          className="p-2.5 rounded-sm hover:bg-[#EDE8DF] transition-colors"
          data-testid="go-to-settings"
        >
          <Settings size={20} strokeWidth={1.5} className="text-[#5C5347]" />
        </button>
      </header>

      <main className="px-6 max-w-lg mx-auto space-y-4">
        {/* Profile Preview Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-sm text-[#F0EBE3] relative overflow-hidden"
          style={{ backgroundColor: '#2C2520' }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#F7F4EF]/5 rounded-full -translate-y-8 translate-x-8" />
          <p className="v4-eyebrow mb-2" style={{ color: '#C67A5C' }}>GetChosenFirst Verified</p>
          <h2 className="font-display text-xl font-medium mb-1" data-testid="profile-name">{business?.business_name}</h2>
          <p className="text-[#F0EBE3]/70 text-sm mb-1">{profile?.tagline || business?.profession}</p>
          <div className="flex items-center gap-1.5 text-[#F0EBE3]/60 text-xs">
            <MapPin size={12} strokeWidth={1.5} />
            <span>{business?.city}{business?.state ? `, ${business.state}` : ''}</span>
          </div>

          <div className="flex gap-2 mt-4">
            <a
              href={`/p/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 bg-[#F0EBE3]/15 hover:bg-[#EDE8DF]/25 text-[#F0EBE3] text-xs font-medium px-3.5 py-2 rounded-none transition-colors"
              data-testid="view-public-page"
            >
              <ExternalLink size={13} strokeWidth={2} /> View Public Page
            </a>
            <button
              onClick={copyProfileUrl}
              className="flex items-center gap-1.5 bg-[#F0EBE3]/15 hover:bg-[#EDE8DF]/25 text-[#F0EBE3] text-xs font-medium px-3.5 py-2 rounded-none transition-colors"
              data-testid="share-profile-btn"
            >
              {copied ? <><Check size={13} strokeWidth={2} /> Copied!</> : <><Copy size={13} strokeWidth={2} /> Share</>}
            </button>
          </div>
        </motion.div>

        {/* Edit Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-[#3C3226]">Business Info</h3>
            <button
              onClick={() => editing ? saveProfile() : setEditing(true)}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-none transition-colors ${
                editing
                  ? 'bg-[#3C3226] text-[#F0EBE3] hover:opacity-85'
                  : 'bg-[#F7F4EF] text-[#3C3226] hover:bg-[#D4C9B8]'
              }`}
              data-testid="edit-profile-btn"
            >
              {editing ? <><Check size={13} /> Save</> : <><Edit3 size={13} /> Edit</>}
            </button>
          </div>

          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#5C5347] mb-1 block">Tagline</label>
                <input
                  type="text"
                  value={editForm.tagline}
                  onChange={e => setEditForm(f => ({ ...f, tagline: e.target.value }))}
                  className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
                  placeholder="Your professional tagline"
                  data-testid="edit-tagline"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5347] mb-1 block">Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={e => setEditForm(f => ({ ...f, bio: e.target.value }))}
                  className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Tell customers about your business"
                  data-testid="edit-bio"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#5C5347] mb-1 block">Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
                    data-testid="edit-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C5347] mb-1 block">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
                    data-testid="edit-email"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#5C5347] mb-1 block">Website</label>
                <input
                  type="url"
                  value={editForm.website}
                  onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))}
                  className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
                  placeholder="www.yourbusiness.com"
                  data-testid="edit-website"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {profile?.tagline && (
                <p className="text-sm text-[#3C3226] italic">"{profile.tagline}"</p>
              )}
              {profile?.bio && (
                <p className="text-sm text-[#5C5347] leading-relaxed">{profile.bio}</p>
              )}
              <div className="flex flex-wrap gap-3 pt-1">
                {(profile?.phone || business?.phone) && (
                  <span className="flex items-center gap-1.5 text-xs text-[#5C5347]"><Phone size={12} /> {profile?.phone || business?.phone}</span>
                )}
                {(profile?.email || business?.email) && (
                  <span className="flex items-center gap-1.5 text-xs text-[#5C5347]"><Mail size={12} /> {profile?.email || business?.email}</span>
                )}
                {(profile?.website || business?.website) && (
                  <span className="flex items-center gap-1.5 text-xs text-[#5C5347]"><Globe size={12} /> {profile?.website || business?.website}</span>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Linked Platforms */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]"
        >
          <h3 className="text-sm font-medium text-[#3C3226] mb-4">Linked Platforms</h3>
          <div className="space-y-2">
            {linked_platforms?.map((platform) => {
              const Icon = platformIcons[platform.key] || Link2;
              const isLinked = platform.status === 'linked' || platform.status === 'confirmed';
              return (
                <div
                  key={platform.key}
                  className="flex items-center gap-3 py-2"
                  data-testid={`linked-platform-${platform.key}`}
                >
                  <div className={`w-8 h-8 rounded-none flex items-center justify-center flex-shrink-0 ${
                    isLinked ? 'bg-[#1D6B4F]/10' : 'bg-[#F7F4EF]'
                  }`}>
                    <Icon size={15} strokeWidth={1.5} className={isLinked ? 'text-[#1D6B4F]' : 'text-[#5C5347]'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-[#3C3226] block">{platform.name}</span>
                    {platform.url && (
                      <span className="text-xs text-[#5C5347] truncate block">{platform.url}</span>
                    )}
                  </div>
                  {isLinked ? (
                    <CheckCircle2 size={16} strokeWidth={1.5} className="text-[#1D6B4F] flex-shrink-0" />
                  ) : (
                    <span className="text-[10px] text-[#5C5347] bg-[#F7F4EF] px-2 py-0.5 rounded-full">Not linked</span>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Booking Integration */}
        <BookingSection />

        {/* Web Presence / Scrape My Website */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]"
          data-testid="web-presence-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-[#3C3226]">Web Presence</h3>
              <p className="text-xs text-[#5C5347] mt-0.5">What AI sees when it scans your website</p>
            </div>
            <button
              onClick={handleScrape}
              disabled={scraping}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#F7F4EF] text-[#1D6B4F] hover:bg-[#D4C9B8] transition-colors disabled:opacity-50"
              data-testid="scrape-website-btn"
            >
              {scraping ? <><Loader2 size={13} className="animate-spin" /> Scanning...</> : <><RefreshCw size={13} /> {webPresence ? 'Re-scan' : 'Scan Website'}</>}
            </button>
          </div>

          {scrapeError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-sm mb-3" data-testid="scrape-error">
              <AlertCircle size={13} /> {scrapeError}
            </div>
          )}

          {webPresence?.extracted_data ? (
            <div className="space-y-4">
              {/* Services Found */}
              {webPresence.extracted_data.services?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[#5C5347] mb-2">Services we found</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="extracted-services">
                    {webPresence.extracted_data.services.slice(0, 12).map((svc) => (
                      <span key={svc} className="text-[11px] px-2.5 py-1 rounded-none bg-[#3C3226]/8 text-[#1D6B4F] font-medium">{svc}</span>
                    ))}
                    {webPresence.extracted_data.services.length > 12 && (
                      <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#F7F4EF] text-[#5C5347]">+{webPresence.extracted_data.services.length - 12} more</span>
                    )}
                  </div>
                </div>
              )}

              {/* Social Links Found */}
              {Object.keys(webPresence.social_links || {}).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[#5C5347] mb-2">Social profiles detected</p>
                  <div className="space-y-1.5" data-testid="extracted-social-links">
                    {Object.entries(webPresence.social_links).map(([platform, url]) => {
                      const Icon = platformIcons[platform] || Link2;
                      const label = platform.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      return (
                        <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 py-1.5 group" data-testid={`social-link-${platform}`}>
                          <div className="w-7 h-7 rounded-none bg-[#1D6B4F]/10 flex items-center justify-center">
                            <Icon size={13} strokeWidth={1.5} className="text-[#1D6B4F]" />
                          </div>
                          <span className="text-xs text-[#3C3226] group-hover:text-[#1D6B4F] transition-colors">{label}</span>
                          <ExternalLink size={10} className="text-[#5C5347] opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* About Summary */}
              {webPresence.extracted_data.about_summary && (
                <div>
                  <p className="text-xs font-medium text-[#5C5347] mb-1">AI summary</p>
                  <p className="text-xs text-[#5C5347] leading-relaxed" data-testid="extracted-about">{webPresence.extracted_data.about_summary}</p>
                </div>
              )}

              <p className="text-[10px] text-[#BBB] pt-1">Last scanned {new Date(webPresence.scraped_at).toLocaleDateString()}</p>
            </div>
          ) : !scraping ? (
            <div className="text-center py-4">
              <Globe size={28} strokeWidth={1} className="text-[#D4C9B8] mx-auto mb-2" />
              <p className="text-xs text-[#5C5347]">Scan your website to see what AI knows about your business.</p>
              <p className="text-[10px] text-[#BBB] mt-1">This data improves your scan accuracy.</p>
            </div>
          ) : null}
        </motion.div>

        {/* Profile URL */}
        {profileUrl && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8]"
          >
            <p className="text-xs font-medium text-[#5C5347] mb-1.5">Your public profile URL</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-[#3C3226] bg-[#F7F4EF] px-3 py-2 rounded-none truncate" data-testid="profile-url">
                {profileUrl}
              </code>
              <button
                onClick={copyProfileUrl}
                className="flex-shrink-0 p-2 rounded-none bg-[#F7F4EF] hover:bg-[#D4C9B8] transition-colors"
                data-testid="copy-url-btn"
              >
                {copied ? <Check size={14} className="text-[#1D6B4F]" /> : <Copy size={14} className="text-[#5C5347]" />}
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
    </AppShell>
  );
}


/* ── Booking Integration Section ── */
function BookingSection() {
  const [bookingUrl, setBookingUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editUrl, setEditUrl] = useState('');

  useEffect(() => {
    axios.get(`${API}/profile/booking`, { withCredentials: true })
      .then(r => {
        setBookingUrl(r.data.booking_url || '');
        setEditUrl(r.data.booking_url || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await axios.put(`${API}/profile/booking`, { booking_url: editUrl.trim() }, { withCredentials: true });
      setBookingUrl(res.data.booking_url || '');
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { /* save failed */ }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.11 }}
      className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]"
      data-testid="booking-section"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={16} strokeWidth={1.5} className="text-[#B8985A]" />
          <h3 className="text-sm font-medium text-[#3C3226]">Booking Integration</h3>
        </div>
        {bookingUrl && !editing && (
          <button
            onClick={() => { setEditing(true); setEditUrl(bookingUrl); }}
            className="text-xs font-medium text-[#5C5347] hover:text-[#3C3226]"
            data-testid="edit-booking-btn"
          >
            <Edit3 size={13} className="inline mr-1" />Edit
          </button>
        )}
      </div>

      <p className="text-xs text-[#5C5347] mb-3">
        Connect your Calendly or Cal.com link. A booking widget will appear on your public profile.
      </p>

      {editing || !bookingUrl ? (
        <div>
          <input
            type="url"
            value={editUrl}
            onChange={e => setEditUrl(e.target.value)}
            placeholder="https://calendly.com/your-name or https://cal.com/your-name"
            className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
            data-testid="booking-url-input"
          />
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-none transition-all"
              style={{ backgroundColor: '#2C2520', color: '#F0EBE3', opacity: saving ? 0.6 : 1 }}
              data-testid="save-booking-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : saved ? <Check size={12} /> : <Calendar size={12} />}
              {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
            </button>
            {bookingUrl && (
              <button
                onClick={() => { setEditing(false); setEditUrl(bookingUrl); }}
                className="text-xs text-[#5C5347] px-3 py-2"
                data-testid="cancel-booking-btn"
              >
                Cancel
              </button>
            )}
          </div>
          <p className="text-[10px] text-[#8C7E6E] mt-2">Supports Calendly and Cal.com URLs</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-3 rounded-sm" style={{ backgroundColor: '#1D6B4F08', border: '1px solid #1D6B4F20' }}>
          <CheckCircle2 size={16} className="text-[#1D6B4F] flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-[#1D6B4F]">Booking widget active</p>
            <p className="text-[11px] text-[#5C5347] truncate">{bookingUrl}</p>
          </div>
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#1D6B4F] hover:underline flex-shrink-0"
            data-testid="booking-preview-link"
          >
            <ExternalLink size={13} />
          </a>
        </div>
      )}
    </motion.div>
  );
}
